import { DragSource } from 'react-dnd';
import GS from '../../grafsync/grafsync';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import PropTypes from 'prop-types';
import React from 'react';
import Notification from '../../core/Notification';
import LanguageAdornment from '../../core/LanguageAdornment';
import { globalStyles } from '../../core/style/gstheme';
import withGS from '../../core/withGShoc';
import { Typography } from '@material-ui/core';
import { STATUS_ACCEPTED, STATUS_REJECTED } from '../../core/utils/constants';
import { isRTL } from '../../core/utils/utils';

const APPROVED = STATUS_ACCEPTED;
const REJECTED = STATUS_REJECTED;

const styles = theme => {
    return Object.assign({
        notInScheme: {
            border: 'solid 1px #CA4F0C',
            borderRadius: '4px',
            padding: '4px 8px 4px 12px',
            height: 24,
            marginBottom: 4
        },
        notInSchemeText: {
            color: '#CA4F0C',
            display: 'inline-block',
            marginTop: 2, maxWidth: 'calc(100% - 30px)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        linkInput: {
            cursor: 'pointer'
        },
        linkField: {
            '&:hover': {
                cursor: 'pointer'
            }
        },
        xl: {
            color: theme.palette.primary.main,
            fontSize: '14px',
            borderRadius: '50%',
            fontWeight: 500,
            padding: '6px',
            marginRight: '-6px'
        }
    }, globalStyles);
};

const labelLoadOptions = {
    predicates: [
        "http://www.w3.org/2008/05/skos-xl#literalForm",
        "http://www.w3.org/2004/02/skos/core#inScheme",
        "https://grafsync.graphifi.com/schema/gs/status"
    ]
};

const Types = {
    XLLABEL: 'xllabel'
};

const labelSource = {
    beginDrag(props) {
        return {
            labelId: props.id,
            label: props.data.literalForm[Object.keys(props.data.literalForm)[0]],
            labelLang: Object.keys(props.data.literalForm)[0],
            type: "Label"
        };
    }
};

function collectSource(connect) {
    return {
        connectDragSource: connect.dragSource()
    };
}

class XLsimpleLabel extends React.Component {

    jumpToLabel = () => {
        if (this.props.data.inScheme && this.props.data.inScheme.length > 0) {
            this.props.showLabelDetail(this.props.id, this.props.data.inScheme[0]);
        }
        else {
            this.props.showFloatingLabel(this.props.id);
        }
    };

    addToScheme = () => {
        GS.addProperty(this.props.id, this.props.graph, 'inScheme', this.props.schemeIRI);
    };

    render() {

        const { connectDragSource, classes, data, onBreakLink, onConvertLabel, draggable, useWorkflow,
            isReadOnly, canBreakIfDefault, defaultLang, ariaContext } = this.props;

        if (!data.literalForm) return <Typography variant="body2" style={{ color: '#DD2C00' }}>Missing literal form</Typography>;

        const lang = Object.keys(data.literalForm)[0];
        const value = data.literalForm[lang];
        const rtl = isRTL(lang);

        const xlsl = <div>
            <TextField
                data-test={"simple-label-" + value}
                multiline
                margin="dense"
                name="literalForm"
                style={{ marginTop: 0 }}
                fullWidth
                className={classes.linkField}
                onClick={this.jumpToLabel}
                value={value}
                variant="outlined"
                InputProps={{
                    inputProps: {
                        "data-test": "simple-label-input",
                        "aria-label": ariaContext + " literal form, " + data.id ,
                        ...(rtl) && { dir: "rtl" },
                        "lang": lang
                    },
                    className: classes.inputLinkLabel,
                    classes: {
                        input: classes.linkInput
                    },
                    readOnly: true,
                    startAdornment: (<LanguageAdornment lang={lang} />),
                    endAdornment:
                        <InputAdornment position="end">
                            {!isReadOnly &&
                                <React.Fragment>
                                    <Tooltip placement="right" title='Convert to basic label' className={classes.smallIconButton}>
                                        <IconButton
                                            data-test="convert-label"
                                            aria-label="Convert to basic label"
                                            onClick={(ev) => {
                                                onConvertLabel(data.id, value, lang);
                                                ev.preventDefault();
                                                ev.stopPropagation();
                                            }}>
                                            <SwapHorizIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {(lang !== defaultLang || (lang === defaultLang && canBreakIfDefault)) &&
                                        <Tooltip placement="right" title='Break relationship to label' className={classes.smallIconButton}>
                                            <IconButton
                                                data-test="break-relatonship"
                                                aria-label="Break relationship to label"
                                                onClick={(ev) => {
                                                    onBreakLink(data.id);
                                                    ev.preventDefault();
                                                    ev.stopPropagation();
                                                }}>
                                                <LinkOffIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                </React.Fragment>
                            }
                            <div className={classes.xl}>XL</div>
                        </InputAdornment>
                }} />
            {useWorkflow && (!data.status || (data.status && data.status[0] !== APPROVED)) &&
                <Notification
                    dataTest="label-status-issue"
                    error={data.status && data.status[0] === REJECTED}
                    text={
                        data.status && data.status[0] === REJECTED ?
                            "The XL label above has been rejected."
                            :
                            "The XL label above is not approved."
                    } />
            }
            {!isReadOnly && (!data.inScheme || data.inScheme.length === 0) &&
                <div data-test="label-not-in-scheme" className={classes.notInScheme}>
                    <Typography variant="body2" className={classes.notInSchemeText}>The XL label above is not in any scheme.</Typography>
                    {this.props.schemeIRI &&
                        <span className={classes.slatButton} style={{ display: 'inline-block', float: 'right' }}>
                            <Tooltip title="Add to concept scheme"
                                onClick={() => this.addToScheme()}>
                                <IconButton
                                    aria-label="Add to concept scheme"
                                    style={{ height: 24, width: 24, minHeight: 24, minWidth: 24, color: 'white' }}
                                    data-test="add-to-scheme"
                                    className={classes.smallIconButton}>
                                    <BubbleChartIcon />
                                </IconButton>
                            </Tooltip>
                        </span>
                    }
                </div>
            }
        </div>;

        return isReadOnly || !draggable ? xlsl : connectDragSource(xlsl);

    }
}

XLsimpleLabel.defaultProps = {
    draggable: false,
    isReadOnly: false,
    canBreakIfDefault: true,
    useWorkflow: false,
    colour: "green"
};

XLsimpleLabel.propTypes = {
    ariaContext: PropTypes.string,
    draggable: PropTypes.bool,
    graph: PropTypes.string.isRequired,
    schemeIRI: PropTypes.string,
    useWorkflow: PropTypes.bool,
    data: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    showLabelDetail: PropTypes.func.isRequired,
    showFloatingLabel: PropTypes.func.isRequired,
    canBreakIfDefault: PropTypes.bool.isRequired,
    onBreakLink: PropTypes.func.isRequired,
    defaultLang: PropTypes.string.isRequired,
    onConvertLabel: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool
};

const xsl = DragSource(Types.XLLABEL, labelSource, collectSource)(withStyles(styles)(XLsimpleLabel));

export default withGS(xsl, ["ALL"], ["id", "literalForm", "inScheme", "status"], labelLoadOptions);